import { HourType } from "../components/Badges";
import { ScheduleRemark } from "../services/apiService";

export interface SchedulePatchChange {
  op: "upsert" | "delete";
  person_id: number;
  activity_id: number;
  week_id: number;
  hours?: number;
  comment?: string | null;
  activity?: string;
  client?: string;
  project?: string;
  hour_type?: string;
  reference?: string;
  reservation?: boolean;
  person_name?: string;
}

export interface SchedulePatch {
  type: "schedule.patch";
  origin: string;
  client_mutation_id?: string;
  revision?: number;
  changes: SchedulePatchChange[];
}

export interface PatchProject {
  id: string;
  activityId: number;
  name: string;
  activity: string;
  client: string;
  project: string;
  hourType: HourType;
  reference: string;
  weeklyHours: { [weekKey: string]: number };
  comments: { [weekKey: string]: string };
  isReservation: boolean;
}

export interface PatchEmployee {
  id: number;
  name: string;
  availability: { [weekKey: string]: number };
  projects: PatchProject[];
  scheduleRemarks: ScheduleRemark[];
}

function buildProjectName(
  client: string,
  project: string,
  activity: string,
): string {
  return `${client} - ${project} ${activity}`;
}

function hasWeeklyHours(project: PatchProject): boolean {
  return Object.keys(project.weeklyHours).length > 0;
}

function applyChangeToEmployee<T extends PatchEmployee>(
  employee: T,
  change: SchedulePatchChange,
  weekKey: string,
): { employee: T; changed: boolean; error?: string } {
  const activityIdStr = change.activity_id.toString();
  const projectIndex = employee.projects.findIndex(
    (p) => p.activityId === change.activity_id,
  );

  let project: PatchProject | undefined =
    projectIndex >= 0 ? employee.projects[projectIndex] : undefined;

  const oldHours = project?.weeklyHours[weekKey] || 0;
  const newHours = change.op === "delete" ? 0 : (change.hours ?? 0);

  const currentAvailability = employee.availability[weekKey] || 0;
  const newAvailability = currentAvailability + oldHours - newHours;

  let newAvailabilityMap = employee.availability;
  if (newAvailability !== currentAvailability) {
    newAvailabilityMap = { ...employee.availability, [weekKey]: newAvailability };
  }

  if (change.op === "delete") {
    if (!project) {
      return { employee, changed: false };
    }

    const { [weekKey]: _h, ...remainingHours } = project.weeklyHours;
    const { [weekKey]: _c, ...remainingComments } = project.comments;

    const updatedProject: PatchProject = {
      ...project,
      weeklyHours: remainingHours,
      comments: remainingComments,
    };

    let newProjects: PatchProject[];
    if (hasWeeklyHours(updatedProject)) {
      newProjects = [...employee.projects];
      newProjects[projectIndex] = updatedProject;
    } else {
      newProjects = employee.projects.filter((_, i) => i !== projectIndex);
    }

    return {
      employee: {
        ...employee,
        availability: newAvailabilityMap,
        projects: newProjects,
      },
      changed: true,
    };
  }

  // upsert
  if (!project) {
    if (
      !change.activity ||
      !change.client ||
      !change.project ||
      !change.hour_type ||
      change.reference === undefined
    ) {
      return {
        employee,
        changed: false,
        error:
          "Missing denormalized fields for new activity row in schedule patch",
      };
    }

    project = {
      id: activityIdStr,
      activityId: change.activity_id,
      name: buildProjectName(change.client, change.project, change.activity),
      activity: change.activity,
      client: change.client,
      project: change.project,
      hourType: change.hour_type as HourType,
      reference: change.reference,
      weeklyHours: {},
      comments: {},
      isReservation: change.reservation ?? false,
    };
  }

  const updatedWeeklyHours = { ...project.weeklyHours, [weekKey]: newHours };
  const updatedComments = { ...project.comments };
  if (change.comment !== undefined) {
    if (change.comment === null || change.comment === "") {
      delete updatedComments[weekKey];
    } else {
      updatedComments[weekKey] = change.comment;
    }
  }

  const updatedProject: PatchProject = {
    ...project,
    weeklyHours: updatedWeeklyHours,
    comments: updatedComments,
    ...(change.reservation !== undefined
      ? { isReservation: change.reservation }
      : {}),
  };

  let newProjects: PatchProject[];
  if (projectIndex >= 0) {
    newProjects = [...employee.projects];
    newProjects[projectIndex] = updatedProject;
  } else {
    newProjects = [...employee.projects, updatedProject];
  }

  return {
    employee: {
      ...employee,
      availability: newAvailabilityMap,
      projects: newProjects,
    },
    changed: true,
  };
}

export function applySchedulePatch<T extends PatchEmployee>(
  employees: T[],
  patch: SchedulePatch,
  visibleWeekKeys?: Set<string>,
): { employees: T[]; applied: boolean; error?: string } {
  if (!patch.changes || patch.changes.length === 0) {
    return { employees, applied: false };
  }

  let result = employees;
  let applied = false;

  for (const change of patch.changes) {
    const weekKey = String(change.week_id);
    if (visibleWeekKeys && !visibleWeekKeys.has(weekKey)) {
      continue;
    }

    const employeeIndex = result.findIndex((e) => e.id === change.person_id);
    let employee: T | undefined =
      employeeIndex >= 0 ? result[employeeIndex] : undefined;

    if (!employee) {
      if (!change.person_name) {
        return {
          employees,
          applied: false,
          error: "Missing person_name for new employee in schedule patch",
        };
      }
      employee = {
        id: change.person_id,
        name: change.person_name,
        availability: {},
        projects: [],
        scheduleRemarks: [],
      } as unknown as T;
    }

    const changeResult = applyChangeToEmployee(employee, change, weekKey);
    if (changeResult.error) {
      return { employees, applied: false, error: changeResult.error };
    }
    if (!changeResult.changed) {
      continue;
    }

    applied = true;
    const newEmployees = [...result];
    if (employeeIndex >= 0) {
      newEmployees[employeeIndex] = changeResult.employee;
    } else {
      newEmployees.push(changeResult.employee);
    }
    result = newEmployees;
  }

  return { employees: result, applied };
}
